'use client'

import { useEffect, useRef, type MouseEvent, type KeyboardEvent } from 'react'
import { findOptionByValue, getOptionDisplayText } from 'shared-utils/combobox'
import type { TPktComboboxDisplayValue } from 'shared-types/combobox'
import { PktTag } from '../tag/Tag'
import type { IComboboxState } from './types'

interface IComboboxTagsProps {
  state: IComboboxState
  outside?: boolean
}

export const ComboboxTags = ({ state, outside = false }: IComboboxTagsProps) => {
  const { values, options, disabled, displayValueAs, handleTagRemove, handleTagKeydown, focusedTagIndex } = state
  const tagRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    if (!outside && focusedTagIndex >= 0 && focusedTagIndex < values.length) {
      tagRefs.current[focusedTagIndex]?.focus()
    }
  }, [outside, focusedTagIndex, values.length])

  if (values.length === 0) return null

  const renderTag = (val: string, index: number) => {
    const option = findOptionByValue(options, val)
    const tagSkinColor = option?.tagSkinColor
    const displayText = option ? getOptionDisplayText(option, displayValueAs) : val

    if (disabled) {
      return (
        <li key={val} role="listitem">
          <PktTag skin={tagSkinColor || 'blue-dark'}>
            <span className="pkt-combobox__value">{displayText}</span>
          </PktTag>
        </li>
      )
    }

    // Outside tags: normal tabbable buttons, no roving tabindex
    if (outside) {
      return (
        <li key={val} role="listitem">
          <PktTag skin={tagSkinColor || 'blue-dark'} closeTag onClose={() => handleTagRemove(val)}>
            <span className="pkt-combobox__value">{displayText}</span>
          </PktTag>
        </li>
      )
    }

    // Inside tags: roving tabindex, arrow key navigation
    return (
      <li
        key={val}
        role="listitem"
        onClick={(e: MouseEvent) => e.stopPropagation()}
        onMouseDown={(e: MouseEvent) => e.preventDefault()}
      >
        <PktTag
          ref={(el: HTMLButtonElement | null) => {
            tagRefs.current[index] = el
          }}
          skin={tagSkinColor || 'blue-dark'}
          closeTag
          tabIndex={focusedTagIndex === index ? 0 : -1}
          onClose={() => handleTagRemove(val)}
          onKeyDown={(e: KeyboardEvent) => handleTagKeydown(e, index)}
        >
          <span className="pkt-combobox__value">{displayText}</span>
        </PktTag>
      </li>
    )
  }

  const tags = values.map(renderTag)

  if (outside) {
    return (
      <div className="pkt-combobox__tags-outside">
        <ul role="list" className="pkt-combobox__tag-list">
          {tags}
        </ul>
      </div>
    )
  }

  return (
    <ul role="list" className="pkt-combobox__tag-list">
      {tags}
    </ul>
  )
}

interface ISingleValueDisplayProps {
  state: IComboboxState
}

export const SingleValueDisplay = ({ state }: ISingleValueDisplayProps) => {
  const { values, options, editingSingleValue, displayValueAs } = state

  if (editingSingleValue || values.length === 0) return null

  const option = findOptionByValue(options, values[0])
  if (!option) return null

  const displayText = getOptionDisplayText(option, displayValueAs as TPktComboboxDisplayValue)

  return <span className="pkt-combobox__value">{displayText}</span>
}
